// Copyright (c) 2026 Solartek LLC. All rights reserved.
import { z } from 'zod';
import { REPORT_FORMATS, REPORT_TYPES } from '../enums.js';
import { codeSchema, isoDateSchema, paginationQuerySchema, timeSchema, uuidSchema } from '../common.js';
import { MONTHLY_ATTENDANCE_LAYOUTS, reportParametersSchema } from '../reports.js';
import { updateSchemaOf } from './devices.js';
import { REPORT_TYPE_DEFINITIONS } from './reports.js';
/**
 * Report sharing and schedules (HR portal Prompt 6a). A share ("Send now") or a scheduled run is a DISTRIBUTION of a report
 * type + filters: every recipient receives a report generated under THEIR OWN access scope (the exact parameters
 * POST /reports would build if that recipient asked for it) — recipients who could not request the report themselves are
 * skipped with a reason, never sent a copy of the sender's data. The file is fetched through the recipient's authenticated
 * session (a 5-minute signed URL minted on click); no bearer link ever leaves the application.
 */
/** `daily` (migration 20261019000500): every day at the run time — the morning report on yesterday's irregular cases. */
export const REPORT_SCHEDULE_CADENCES = ['monthly', 'weekly', 'daily'];
export const REPORT_PERIOD_RULES = ['previous_month', 'month_to_date', 'previous_week', 'custom', 'previous_day'];
/** Period rules a cadence may use (mirrors report_schedules_cadence_period_check). */
export const PERIOD_RULES_BY_CADENCE = {
    monthly: ['previous_month', 'month_to_date', 'custom'],
    weekly: ['previous_week', 'month_to_date'],
    daily: ['previous_day', 'month_to_date'],
};
/** A daily schedule has no run day; it is stored as 0 (report_schedules_run_day_check). */
export const DAILY_SCHEDULE_RUN_DAY = 0;
export const REPORT_DELIVERY_CHANNELS = ['in_app', 'email'];
/** `cancelled`: the recipient cancelled their queued copy (HR portal Prompt 6a review — minor 14). */
export const REPORT_DELIVERY_STATUSES = ['queued', 'delivered', 'skipped', 'failed', 'cancelled'];
export const REPORT_DELIVERY_MODES = ['schedule', 'manual', 'send_now'];
export const REPORT_SCHEDULE_RUN_STATUSES = ['success', 'partial', 'failed', 'skipped'];
export const REPORT_RECIPIENT_MAX_USERS = 50;
export const REPORT_RECIPIENT_MAX_ROLES = 10;
/** Resolved recipients of one share / run (explicit users + holders of the chosen roles), after de-duplication. */
export const REPORT_RECIPIENT_MAX_RESOLVED = 100;
/**
 * Line managers one run reaches through `recipients.lineManagers` (each copy is their team only, so a small report): counted apart
 * from REPORT_RECIPIENT_MAX_RESOLVED, which bounds the people chosen by name or role.
 */
export const REPORT_RECIPIENT_MAX_LINE_MANAGERS = 500;
/** Shares + run-now per organisation per hour (every share fans out to up to REPORT_RECIPIENT_MAX_RESOLVED generations). */
export const REPORT_SHARES_PER_HOUR = 10;
/**
 * Report types a schedule / share can carry: every available type whose period can be derived from a date range — the Daily
 * Report included since the `daily` cadence (yesterday; a weekly or monthly run prints each day of the period, ≤ 62 days).
 */
export const SCHEDULABLE_REPORT_TYPES = REPORT_TYPE_DEFINITIONS
    .filter((d) => d.status === 'available')
    .map((d) => d.key);
/** Types whose period parameter is a whole month (`month`): only whole-month rules make sense for them. */
export const MONTH_PARAMETER_REPORT_TYPES = REPORT_TYPE_DEFINITIONS.filter((d) => d.requiredParameters.includes('month')).map((d) => d.key);
/** Types that print the week containing `from`: only the previous-week rule produces one whole week. */
export const WEEK_PARAMETER_REPORT_TYPES = REPORT_TYPE_DEFINITIONS
    .filter((d) => d.requiredParameters.includes('from') && !d.requiredParameters.includes('to') && d.key !== 'daily_attendance')
    .map((d) => d.key);
/** The report's own parameters minus the period (which the period rule derives at run time). */
export const reportScheduleFiltersSchema = z.object({
    branchId: uuidSchema.optional(),
    /** As `reportParametersSchema.locationId`: a group / branch location → its branches; a place → the employees working in it or below. */
    locationId: uuidSchema.optional(),
    departmentId: uuidSchema.optional(),
    employeeIds: z.array(uuidSchema).min(1).max(500).optional(),
    leaveTypeCode: codeSchema.optional(),
    employmentStatus: z.enum(['active', 'inactive', 'all']).optional(),
    scope: z.enum(['attendance', 'all']).optional(),
    layout: z.enum(MONTHLY_ATTENDANCE_LAYOUTS).optional(),
    locale: z.enum(['en', 'ar']).optional(),
}).strict();
export const reportRecipientsSchema = z.object({
    /** Specific members (including the managers picked in the Managers list). */
    userIds: z.array(uuidSchema).max(REPORT_RECIPIENT_MAX_USERS).default([]),
    /** Every active member holding one of these roles at run time (e.g. hr_admin). */
    roleKeys: z.array(z.string().trim().regex(/^[a-z][a-z0-9_]{1,63}$/)).max(REPORT_RECIPIENT_MAX_ROLES).default([]),
    /**
     * Every line manager at run time — an active member whose employee record is the primary manager of at least one active
     * employee — receives a copy about THEIR team only (`scopeReportForRecipient`, scope TEAM), even without report.export, and only
     * when the report's filters leave them at least one direct report.
     */
    lineManagers: z.boolean().default(false),
}).refine((r) => r.userIds.length + r.roleKeys.length > 0 || r.lineManagers, { message: 'Choose at least one recipient', path: ['userIds'] });
const channelsSchema = z.array(z.enum(REPORT_DELIVERY_CHANNELS)).min(1).max(2).transform((c) => [...new Set(c)]);
const periodRuleRefinement = (v, ctx) => {
    if (v.cadence && v.periodRule && !PERIOD_RULES_BY_CADENCE[v.cadence].includes(v.periodRule))
        ctx.addIssue({ code: 'custom', path: ['periodRule'], message: `A ${v.cadence} schedule cannot use ${v.periodRule}` });
    if (v.cadence === 'monthly' && v.runDay !== undefined && (v.runDay < 1 || v.runDay > 28))
        ctx.addIssue({ code: 'custom', path: ['runDay'], message: 'Run day must be 1–28' });
    if (v.cadence === 'weekly' && v.runDay !== undefined && (v.runDay < 0 || v.runDay > 6))
        ctx.addIssue({ code: 'custom', path: ['runDay'], message: 'Run day must be a weekday (0–6)' });
    if (v.cadence === 'daily' && v.runDay !== undefined && v.runDay !== DAILY_SCHEDULE_RUN_DAY)
        ctx.addIssue({ code: 'custom', path: ['runDay'], message: 'A daily schedule has no run day (0)' });
    if (v.periodRule === 'custom' && (v.customFromDay == null || v.customToDay == null))
        ctx.addIssue({ code: 'custom', path: ['customFromDay'], message: 'A custom period needs a from day and a to day' });
    if (v.periodRule === 'custom' && v.customFromDay != null && v.customToDay != null && v.customFromDay <= v.customToDay) {
        ctx.addIssue({ code: 'custom', path: ['customFromDay'], message: 'A custom period runs from a day of the previous month to an earlier day of the next (e.g. 26 → 25)' });
    }
    if (v.reportType && !SCHEDULABLE_REPORT_TYPES.includes(v.reportType))
        ctx.addIssue({ code: 'custom', path: ['reportType'], message: 'This report type cannot be scheduled' });
    if (v.reportType && MONTH_PARAMETER_REPORT_TYPES.includes(v.reportType) && v.periodRule && v.periodRule !== 'previous_month' && v.periodRule !== 'month_to_date') {
        ctx.addIssue({ code: 'custom', path: ['periodRule'], message: 'This report covers a whole month: use previous month or month to date' });
    }
    if (v.reportType && WEEK_PARAMETER_REPORT_TYPES.includes(v.reportType) && v.periodRule && v.periodRule !== 'previous_week') {
        ctx.addIssue({ code: 'custom', path: ['periodRule'], message: 'This report covers one week: use a weekly schedule with the previous week' });
    }
};
const scheduleShape = {
    name: z.string().trim().min(1).max(120),
    reportType: z.enum(REPORT_TYPES),
    format: z.enum(REPORT_FORMATS).default('pdf'),
    filters: reportScheduleFiltersSchema.default({}),
    cadence: z.enum(REPORT_SCHEDULE_CADENCES),
    /** monthly: day of month 1–28; weekly: 0 = Sunday … 6 = Saturday; daily: 0. */
    runDay: z.coerce.number().int().min(0).max(28),
    /** Local time (organisation timezone) the run starts. */
    runTime: timeSchema.default('07:00'),
    periodRule: z.enum(REPORT_PERIOD_RULES),
    /** custom: the period runs from `customFromDay` of the previous month to `customToDay` of the run month. */
    customFromDay: z.coerce.number().int().min(1).max(28).nullish(),
    customToDay: z.coerce.number().int().min(1).max(28).nullish(),
    recipients: reportRecipientsSchema,
    channels: channelsSchema.default(['in_app', 'email']),
    isActive: z.boolean().default(true),
};
export const createReportScheduleSchema = z.object(scheduleShape).superRefine(periodRuleRefinement);
/** PATCH: every field optional and default-free (a `.partial()` would re-apply the defaults and overwrite stored values). */
export const updateReportScheduleSchema = updateSchemaOf(scheduleShape);
export const reportScheduleListQuerySchema = paginationQuerySchema.extend({ reportType: z.enum(REPORT_TYPES).optional() });
export const reportDeliveryListQuerySchema = paginationQuerySchema.extend({
    scheduleId: uuidSchema.optional(),
    status: z.enum(REPORT_DELIVERY_STATUSES).optional(),
    mode: z.enum(REPORT_DELIVERY_MODES).optional(),
});
/** POST /orgs/:orgId/reports/share — generate now for every recipient under their own scope. */
export const shareReportSchema = z.object({
    reportType: z.enum(REPORT_TYPES),
    format: z.enum(REPORT_FORMATS).default('pdf'),
    /** Same parameters as POST /reports (period included); recipient scopes are applied per recipient by the worker. */
    parameters: reportParametersSchema.default({}),
    recipients: reportRecipientsSchema,
    channels: channelsSchema.default(['in_app', 'email']),
    note: z.string().trim().max(500).optional(),
});
/** Occurrence period stored with a delivery (inclusive local dates). */
export const reportPeriodSchema = z.object({ from: isoDateSchema, to: isoDateSchema });
//# sourceMappingURL=report-schedules.js.map